/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import SearchableSelect from "components/SearchableSelect/SearchableSelect";
import useEventRouting from "hooks/useEventRouting";
import { searchAirport } from "lib/airportApi";
import { useEffect, useState } from "react";
import formatSelectOptions from "utils/formatSelectOptions";

const useAirportSearch = (
  args: any = {},
): [
  React.ComponentType<any>,
  {
    options: any[];
    isLoading: boolean;
    onSearch: (search: string) => Promise<any[]>;
  },
] => {
  const [state, setState] = useState<{
    options: Array<{ label: string; value: string; id: string }>;
    isLoading: boolean;
  }>({
    options: [],
    isLoading: false,
  });

  const { eventId } = useEventRouting();
  useEffect(() => {
    if (args?.shouldReset) {
      setState({
        options: [],
        isLoading: false,
      });
    }
    if (args && args.hasOwnProperty("options") && args.options.length > 0) {
      const hasChanged =
        JSON.stringify(state.options) !== JSON.stringify(args.options);
      if (
        args.options[0].hasOwnProperty("id") &&
        args.options[0].hasOwnProperty("value") &&
        hasChanged
      ) {
        setState((prevState) => ({
          ...prevState,
          options: args.options,
        }));
      }
    }
  }, [args]);
  const onSearchAirport = async (search: string) => {
    if (search === "") {
      setState({ options: [], isLoading: false });
      return [];
    }
    setState((prev) => ({ ...prev, isLoading: true }));

    const { ok, data } = await searchAirport(search, eventId);
    if (ok === false) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return [];
    }

    const options: {
      label: string;
      value: string;
      id: string;
    }[] = formatSelectOptions(
      data,
      null,
      false,
      (option: { name: string; code: string }) => ({
        label: `${option.name} Airport (${option.code})`,
        value: option.code,
        id: option.code,
      }),
    );
    setState({ options, isLoading: false });
    return options;
  };

  return [
    SearchableSelect,
    {
      ...state,
      onSearch: onSearchAirport,
    },
  ];
};

export const getAirportLabel = (option: { label: string; value: string }) => {
  let label = option.label.replace(`Airport (${option.value})`, "").trim();
  return label;
};

export default useAirportSearch;
